import React, { useState } from 'react';
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Scatter, XAxis, YAxis } from 'recharts';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate, formatDuration, formatPace, SOURCE_LABELS } from '../utils/format';
import { ChartPoint, DistanceSeries, dayLabel, domainOf, ProgressMetric, ProgressView } from '../utils/progression';

const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };
const MARGIN = { top: 8, right: 12, bottom: 0, left: 0 };
/** Invisible hit target around each dot, larger than the mark (touch friendly). */
const HIT_RADIUS = 12;

interface DotProps {
  cx?: number;
  cy?: number;
  payload?: ChartPoint;
}

interface Props {
  series: DistanceSeries[];
  view: ProgressView;
  metric: ProgressMetric;
}

/**
 * The progression chart: time on x, pace or finishing time on y (reversed, so faster is up), one line per distance.
 * Hover, focus or tap a dot to read it below the chart; click or press Enter to open its activity.
 */
const RecordsProgressChart: React.FC<Props> = ({ series, view, metric }) => {
  const navigate = useNavigate();
  const [active, setActive] = useState<ChartPoint | null>(null);
  const domain = domainOf(series);

  if (!domain) {
    return null;
  }

  const openActivity = (point: ChartPoint) => {
    if (point.effort.activityId != null) navigate(`/activities/${point.effort.activityId}`);
  };

  const renderDot = (props: DotProps) => {
    const { cx, cy, payload } = props;
    if (cx === undefined || cy === undefined || !payload) return <g />;
    const { effort } = payload;
    const linked = effort.activityId != null;
    const label = [
      payload.name,
      formatDate(effort.date),
      effort.activityName ?? 'Manual time',
      formatDuration(effort.timeS),
      formatPace(effort.paceSPerKm),
      SOURCE_LABELS[effort.source],
    ].join(', ');
    return (
      <g
        role={linked ? 'link' : 'img'}
        aria-label={label}
        tabIndex={0}
        style={{ cursor: linked ? 'pointer' : 'default', outline: 'none' }}
        onMouseEnter={() => setActive(payload)}
        onFocus={() => setActive(payload)}
        onTouchStart={() => setActive(payload)}
        onClick={() => {
          setActive(payload);
          openActivity(payload);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter') openActivity(payload);
        }}
      >
        <circle cx={cx} cy={cy} r={HIT_RADIUS} fill="transparent" />
        <circle cx={cx} cy={cy} r={4} fill={payload.style.color} stroke="var(--surface-1)" strokeWidth={2} />
      </g>
    );
  };

  return (
    <>
      <div className="progress-chart" data-testid="progress-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart margin={MARGIN}>
            <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
            <XAxis
              type="number"
              dataKey="x"
              domain={domain.x}
              tickFormatter={dayLabel}
              tick={AXIS}
              tickCount={5}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={domain.y}
              reversed
              tickFormatter={value => formatDuration(value)}
              tick={AXIS}
              width={64}
            />
            {series.map(s => (
              <Line
                key={`line-${s.code}`}
                data={s.line}
                dataKey="y"
                name={s.name}
                stroke={s.style.color}
                strokeWidth={3}
                strokeDasharray={s.style.dashed ? '6 4' : undefined}
                type={view === 'pb' ? 'stepAfter' : 'linear'}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                legendType="none"
              />
            ))}
            {series.map(s => (
              <Scatter
                key={`dots-${s.code}`}
                data={s.dots}
                dataKey="y"
                name={s.name}
                fill={s.style.color}
                shape={(props: unknown) => renderDot(props as DotProps)}
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="progress-readout" aria-live="polite">
        {active ? (
          <>
            <span className="progress-swatch" style={{ background: active.style.color }} aria-hidden="true" />
            <strong>{active.name}</strong>
            {' · '}
            {formatDate(active.effort.date)}
            {' · '}
            {active.effort.activityName ?? 'Manual time'}
            {' · '}
            <span className="tabular">{formatDuration(active.effort.timeS)}</span>
            {' · '}
            <span className="tabular">{formatPace(active.effort.paceSPerKm)}</span>
            {' · '}
            {SOURCE_LABELS[active.effort.source]}
            {active.effort.activityId != null && (
              <>
                {' · '}
                <Link to={`/activities/${active.effort.activityId}`}>Open activity</Link>
              </>
            )}
          </>
        ) : (
          <span className="muted">Hover, focus or tap a point for details. Click a point to open its activity.</span>
        )}
      </div>
      <p className="muted small mb-0 mt-1">
        {metric === 'pace' ? 'Pace per km' : 'Finishing time'} on the vertical axis, faster is higher. Dates run left to
        right. Thick line: {view === 'pb' ? 'the all-time personal-best sequence' : 'the yearly best'}.
      </p>
    </>
  );
};

export default RecordsProgressChart;
